/**
 * The arithmetic behind the continuous sounds, kept apart from WebAudio so it can be tested:
 * when a foot falls, how a bomb's tick accelerates, how long the siren wails, how hard the
 * ranger is breathing. Everything here is a pure function of sim state.
 */

import { TICK_RATE } from '../sim/types';
import type { Enemy, Ranger } from '../sim/types';

/* ---------------------------------------------------------------------- footsteps */

export type Gait = 'walk' | 'run' | 'crawl';

/** Metres per footfall (per elbow-drag when crawling), and per track-link clatter for a minitank. */
export const STRIDE: Record<Gait, number> = { walk: 0.8, run: 1.15, crawl: 0.6 };
export const TREAD_STRIDE = 0.4;

/** Level and brightness of a footfall by gait, as multipliers on the step sound. */
export const GAIT_GAIN: Record<Gait, number> = { walk: 1, run: 1.45, crawl: 1 };
export const GAIT_RATE: Record<Gait, number> = { walk: 1, run: 1.07, crawl: 1 };

/** Enemy footsteps are mixed a little hot: hearing a patrol before seeing it is the game (01 §10). */
export const ENEMY_STEP_GAIN = 1.9;
/** Above this speed, m/s, a soldier is running (patrols walk at 1.5, hunters run at 3.2). */
const ENEMY_RUN_SPEED = 2.4;

export function rangerGait(r: Ranger): Gait {
  return r.stance === 'crawl' || r.status === 'downed' ? 'crawl' : r.running ? 'run' : 'walk';
}

export function enemyGait(e: Enemy): Gait {
  const dx = e.x - e.prevX;
  const dz = e.z - e.prevZ;
  return Math.sqrt(dx * dx + dz * dz) * TICK_RATE > ENEMY_RUN_SPEED ? 'run' : 'walk';
}

/** Did the distance counter cross a stride boundary since it was last looked at? */
export function footfall(prevTravelled: number, travelled: number, stride: number): boolean {
  return travelled > prevTravelled && Math.floor(travelled / stride) !== Math.floor(prevTravelled / stride);
}

/* --------------------------------------------------------------------- time bombs */

/**
 * A fuse's tick count, counted back from detonation: once a second, then twice, four and eight
 * times a second inside the last 10, 5 and 2 seconds (07 §3). A tick sounds whenever the whole
 * part of this changes, so the rhythm depends only on `fuseTicks`, never on the frame rate.
 */
export function bombTickPhase(fuseTicks: number): number {
  const s = Math.max(0, fuseTicks) / TICK_RATE;
  if (s <= 2) return s * 8;
  if (s <= 5) return 16 + (s - 2) * 4;
  if (s <= 10) return 28 + (s - 5) * 2;
  return 38 + (s - 10);
}

export function bombTicked(prevFuseTicks: number, fuseTicks: number): boolean {
  return fuseTicks < prevFuseTicks && Math.floor(bombTickPhase(prevFuseTicks)) !== Math.floor(bombTickPhase(fuseTicks));
}

/** The tick climbs in pitch over the last ten seconds: playback rate 1 -> 1.35. */
export function bombTickRate(fuseTicks: number): number {
  const s = fuseTicks / TICK_RATE;
  return s >= 10 ? 1 : 1 + (1 - s / 10) * 0.35;
}

/* -------------------------------------------------------------------------- siren */

export const SIREN_SECONDS = 20;

/** The siren wails while the alarm stands, but not for ever: 20 s from the `siren` event. */
export function sirenOn(secondsSinceEvent: number, alert: number): boolean {
  return secondsSinceEvent >= 0 && secondsSinceEvent < SIREN_SECONDS && alert >= 3;
}

/* ---------------------------------------------------------------------- breathing */

/** Seconds per breath (in + out), or 0 when the ranger is not audibly breathing. */
export function breathSeconds(r: Ranger): number {
  if (r.status !== 'active' && r.status !== 'downed') return 0;
  const effort = Math.max(r.winded ? 1 : r.fatigue, r.status === 'downed' ? 0.8 : r.wounds >= 2 ? 0.65 : 0);
  if (effort < 0.6) return 0;
  return 2.2 - 1.2 * Math.min(1, (effort - 0.6) / 0.4);
}

/* --------------------------------------------------------------- the last minute */

export const URGENCY_SECONDS = 60;

export function clockUrgent(clockTicks: number, onGround: boolean): boolean {
  return onGround && clockTicks > 0 && clockTicks < URGENCY_SECONDS * TICK_RATE;
}
